import { useQuery } from '@tanstack/react-query';
import type { CMSFaqItem, CMSTabs as CMSTabsType } from '../schemas';
import { getFaqsByIds } from '@/services/cms/getFaqsByIds';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { hasAnyTabHeading, extractFaqIds } from '@/utils/cms';
import { CMSFaqs } from '../cms-faqs';
import { match } from 'ts-pattern';
import { cmsQueryKeys } from '@/services/cms/cmsQueryKeys';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { CMSPlanCardsWrapper } from '../cms-plan-cards-wrapper';
import { CMSColumns } from '../cms-columns';

interface CMSTabsProps {
  tabsData: CMSTabsType;
  className?: string;
}
const CMSTabs = ({ tabsData, className }: CMSTabsProps) => {
  const ids = extractFaqIds(tabsData);

  const hasTabHeading = hasAnyTabHeading(tabsData);

  const {
    isError,
    isPending,
    data: faqsData,
  } = useQuery({
    queryKey: [...cmsQueryKeys.getTabs, ...ids],
    queryFn: () => getFaqsByIds({ ids }),
    staleTime: Infinity,
    enabled: ids.length > 0,
  });

  return (
    <Tabs
      defaultValue={tabsData.items[0].id}
      className={cn('bg-white', className)}
      data-component="CMSTabs"
    >
      {hasTabHeading && (
        <ScrollArea className="w-full">
          <TabsList>
            {tabsData.items.map((tab) => {
              return (
                <TabsTrigger key={tab.id} value={tab.id}>
                  {tab.tab_heading}
                </TabsTrigger>
              );
            })}
          </TabsList>
          <ScrollBar className="h-0" orientation="horizontal" />
        </ScrollArea>
      )}
      {tabsData.items.map((tab) => {
        const tabContent = tab.content
          .map((content) =>
            match(content)
              .with({ type: 'faqs' }, (faqContent) => ({
                type: 'faqs' as const,
                faqs: faqContent.items.map(
                  (item) =>
                    ({
                      id: item.id,
                      title: item.title,
                      // Chaining operator handles the case where a tab doesn't have FAQs
                      content:
                        faqsData?.find((faq) => faq.id === item.id)?.content ||
                        [],
                    }) satisfies CMSFaqItem
                ),
              }))
              .with({ type: 'plan_cards' } as const, (planCardsData) => ({
                ...planCardsData,
              }))
              .with({ type: 'columns' } as const, (columnsData) => ({
                ...columnsData,
              }))
              .with({ type: 'rich_text' } as const, (richTextData) => ({
                ...richTextData,
              }))
              .otherwise(() => 'Unhandled tab content')
          )
          .filter(Boolean);

        return (
          <TabsContent
            key={tab.id}
            value={tab.id}
            forceMount
            className="mb-8 md:mb-12 lg:mb-20 data-[state=inactive]:hidden"
          >
            {tabContent.map((content, index) =>
              match(content)
                .with({ type: 'faqs' }, ({ faqs }) => {
                  return (
                    <div key={`faq-${tab.id}-${faqs[0].id}`}>
                      {!isError && !isPending ? (
                        <CMSFaqs hasTabHeading={hasTabHeading} faqs={faqs} />
                      ) : null}
                    </div>
                  );
                })
                .with({ type: 'plan_cards' } as const, (planCardsData) => (
                  <CMSPlanCardsWrapper
                    key={index}
                    planCardsData={planCardsData}
                  />
                ))
                .with({ type: 'columns' } as const, (columnsData) => (
                  <CMSColumns
                    className={cn(
                      'mt-4 w-full',
                      // Add text-center for CMSQueueCount
                      '[&[data-component="CMSColumnsStandard"]_[data-component="CMSQueueCount"]]:text-center [&[data-component="CMSColumnsStandard"]_[data-component="CMSQueueCount"]]:mb-5',

                      //Set CMSTextAdvanced component to w-full for CMSColumnsStandard
                      '[&[data-component="CMSColumnsStandard"]_[data-component="CMSTextAdvanced"]]:w-full',

                      //Set CMSCTAButton to w-full in mobile
                      '[&[data-component="CMSColumnsStandard"]_[data-component="CMSCTAButton"]_button]:w-full md:[&[data-component="CMSColumnsStandard"]_[data-component="CMSCTAButton"]_button]:w-auto'
                    )}
                    key={columnsData.id}
                    columnsData={columnsData}
                  />
                ))
                .with({ type: 'rich_text' } as const, (item) => (
                  <div key={item.id}>CMSRichText</div>
                ))
                .otherwise(() => 'Unhandled tab content')
            )}
          </TabsContent>
        );
      })}
    </Tabs>
  );
};
export { CMSTabs };
